import { createNewTask, getTask, updateTaskItems } from '@dyl/apiClients/backendApi';
import LoadingScreen from '@dyl/components/LoadingScreen';
import Skills from '@dyl/constants/Skills';
import TaskTypes from '@dyl/task-types';
import useExitPrompt from '@dyl/hooks/useExitPrompt';
import useMemoObject from '@dyl/hooks/useMemoObject';
import createSnackbarAction from '@dyl/utils/createSnackbarAction';
import { Refresh as RetryIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTitle } from '@dyl/systems/TitleManager';
import TaskEditorHeader from './Header';
import TaskContext from './TaskContext';
import useStyles, { screenSaveCancelStyles, updatedActivityLogStyles } from './styles';
import TaskBackgroundImagesBlock from '@dyl/blocks/TaskBackgroundImagesBlock';
import convertToLocalFormat from './convertToLocalFormat';
import { get, isEqual, without } from 'lodash';
import TaskEndAudio from './TaskEndAudio';
import SectionDividers from '@dyl/components/SectionDividers';
import convertToUploadFormat from './convertToUploadFormat';
import ScreenSaveCancelBlock from '@dyl/blocks/ScreenSaveCancelBlock';
import { Backdrop, Chip } from "@mui/material";
import { Lock as LockIcon, Create as DraftIcon } from '@mui/icons-material';
import withLevel from "@dyl/utils/withLevel";
import ActivityLogs from '@dyl/components/ActivityLogs';
import { useAuth } from '@dyl/systems/UserManager';
import PictureDescriptionModal from '@dyl/blocks/PictureDescriptionModal';
import MissingKeyErrorModal from '@dyl/blocks/MissingKeyErrorModal';
import InformationTextA2ErrorModal from '@dyl/blocks/InformationTextA2ErrorModal';
import SendToAutoMarkerButtons from '@dyl/task-types/A2/ProductiveWriting/SendToAutoMarkerButtons';
import updateArcAutomarkerQuestion from '@dyl/apiClients/backendApi/updateArcAutomarkerQuestion';
import ARCActivityLog from '@dyl/components/ARCActivityLog';
import { useTheme } from '@mui/material/styles';

export default withLevel(TaskEditor);

function TaskEditor({ globalLevel }) {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { user } = useAuth();

    const { taskType: taskTypeId, taskId } = useParams();
    const isNew = !taskId;

    const [persistedData, setPersistedData] = useState(null);
    const [isDirty, setDirty] = useState(false);

    const classes = useStyles();

    const taskType = useMemo(() => TaskTypes[globalLevel.label].find(t => t.id === taskTypeId), [taskTypeId, globalLevel]);
    const isPW = taskType?.id === 'productive_writing';
    const skill = useMemo(() => Skills.find(s => s.value === taskType.skill), [taskType]);

    const [isLoading, setLoading] = useState(!isNew);
    const [isDescriptionError, setDescriptionError] = useState(false);
    const [missingKeyError, setMissingKeyError] = useState(false);
    const [underScoreError, setUnderScoreError] = useState(false);
    const [lockedState, setLockedState] = useState(isNew ? true : null);
    const [isSaveButtonTriggered, setIsSaveButtonTriggered] = useState(false);

    const [arcReload, setArcReload] = useState(false);
    const [showSendAutoMarkerButton, setShowSendAutoMarkerButton] = useState(true);
    const [automarkerResponseModalOpen, setAutomarkerResponseModalOpen] = useState(false);
    const [automarkerResponseError, setAutomarkerResponseError] = useState(false);
    const [returnToDraftModalOpen, setReturnToDraftModalOpen] = useState(false);
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    const [mustSaveBeforeAutomarker, setMustSaveBeforeAutomarker] = useState(false);
    const [isQuestionChanged, setIsQuestionChanged] = useState(false);

    const [data, setData] = useState(() => {
        if (isNew) {
            const initTaskDefinition = {
                anchor: false,
                is_question_changed: false,
                // level_id: globalLevel.level_id,
                task_id_name: '',
                calibration_status: 'uncalibrated',
                items: taskType.createItemsTemplate(),

                context_audio_id: null,

                // Picture editing contains colors
                ...(!!taskType.colors && { colors: taskType.colors }),

                // Add end audio if necessary
                ...(!!taskType.hasEndAudio && { final_audio_id: null }),

                // Add background images properties, specific to the task type
                ...(() => {
                    if (!taskType.backgroundImages) return {};

                    const bgImagesProps = {};

                    for (let i = 0; i < taskType.backgroundImages.count; i++) {
                        bgImagesProps[`background_image${i > 0 ? `_${i + 1}` : ''}`] = '';
                    }

                    return bgImagesProps;
                })(),
            };
            setPersistedData(initTaskDefinition);
            return initTaskDefinition;
        }
    });

    // If not new, load data
    useEffect(() => {
        if (isNew) return;

        (async () => {
            while (true) {
                try {
                    let data = await getTask(globalLevel.level_id, taskTypeId, taskId);
                    data = convertToLocalFormat(data);
                    data = taskType?.convertToLocalFormat?.(data) ?? data;
                    setData(data);
                    setPersistedData(data);
                    setDirty(false);
                    setLoading(false);
                    if (isPW &&data?.task_id !== undefined) {
                    if (data?.is_arc_published === true) {
                      setShowSendAutoMarkerButton(false);
                      setLockedState(false);
                      setMustSaveBeforeAutomarker(false)
                      setIsSaveButtonTriggered(false);
                    } else if (data?.is_arc_published === false) {
                      setShowSendAutoMarkerButton(true);
                      setLockedState(true);
                      setMustSaveBeforeAutomarker(false);
                      setIsSaveButtonTriggered(false);
                    }
                    }
                    return;
                } catch (error) {
                    console.error(error);
                    await new Promise(resolve => enqueueSnackbar(
                        'Failed to load task',
                        {
                            variant: 'error',
                            action: createSnackbarAction({
                                iconComponent: RetryIcon,
                                onClick: resolve,
                            }),
                            persist: true,
                        }
                    ));
                }
            }
        })();

    }, []);
    const title = (() => {
        const humanId = data?.task_id_name;

        if (humanId) {
            return humanId;
        }
        else if (isNew) {
            return `New ${taskType?.label ?? '???'} Task`;
        }
        else {
            return 'Loading...';
        }
    })();

    useTitle({ breadcrumbs: ['Tasks', title] });

    useEffect(() => {
    const normData = normalizeForDirty(data);
    const normPersisted = normalizeForDirty(persistedData);

    if (!normData || !normPersisted) {
        setDirty(false);
        return;
    }
    setDirty(!isEqual(normData, normPersisted));
}, [persistedData, data]);
    useExitPrompt(isDirty);
    useEffect(() => {
        if (!isPW || !persistedData || !data) return;
        const oldItems = JSON.stringify(persistedData.items);
        const newItems = JSON.stringify(data.items);
        const changed = oldItems !== newItems;
        // Only flip from false -> true
        setIsQuestionChanged(prev => prev || changed);
    }, [data?.items, persistedData?.items, isPW]);
    useEffect(() => {
        if (!isPW || !persistedData || !data) return;
        const taskNameChanged =
            (data.task_id_name ?? '').trim() !== (persistedData.task_id_name ?? '').trim();
        const itemsChanged =
            JSON.stringify(persistedData.items) !== JSON.stringify(data.items);
        if (taskNameChanged && !itemsChanged) {
            setIsQuestionChanged(false);
        }
    }, [data, persistedData, isPW]);

    const [isSaving, setSaving] = useState(false);

    const [apiValidationErrorData, setApiValidationErrorData] = useState([]);
    const [localErrors, setLocalErrors] = useState([]);

    const registerLocalError = useCallback((message, cb) => {
        const error = { message, cb };

        setLocalErrors(prev => [...prev, error]);

        return () => setLocalErrors(prev => without(prev, error));
    }, []);

    const theme = useTheme();
    const saveCancelStyles = useMemo(() => {
      if (taskType?.id === 'productive_writing') {
        return screenSaveCancelStyles(theme).inlineBelowActivityLogs;
      }
      return {};
    }, [taskType, theme]);

    const canEditTaskIdName = !isPW || isNew || lockedState === true;
    const hasTaskIdNameChanged =
        isPW &&
        !isNew &&
        ((data?.task_id_name ?? '').trim() !== (persistedData?.task_id_name ?? '').trim());
    const save = async () => {
        if (localErrors.length) {
            enqueueSnackbar('Please complete all required fields', { variant: 'error' });
            for (const { message, cb } of localErrors) {
                console.error(message);
                cb?.();
            }

            return;
        }

        setSaving(true);
        setIsSaveButtonTriggered(true);

        // Reset errors when attempting to upload, helps stabilize some things
        setApiValidationErrorData([]);
        setDescriptionError(false)
        setMissingKeyError(false)
        setUnderScoreError(false)
        setDirty(false);

        let toUpload = data;
        if (isPW && !isNew) {
            toUpload = { ...toUpload, is_arc_published: !lockedState };
        }
        toUpload = convertToUploadFormat(toUpload);
        toUpload = taskType?.convertToUploadFormat?.(toUpload, { isNew }) ?? toUpload;
        toUpload = {
            ...toUpload,
            is_question_changed: isQuestionChanged,
        };
        try {
            if (isNew) {
                let task = await createNewTask(globalLevel.level_id, taskType.id, toUpload);
                const { task_id } = task;
                task = convertToLocalFormat(task);
                task = taskType?.convertToLocalFormat?.(task) ?? task;
                setData(task);
                setPersistedData(task);
                setIsQuestionChanged(false)

                enqueueSnackbar('Successfully created task, reloading...', { variant: 'success' });

                navigate(`/${globalLevel.label}/tasks/${taskType.id}/${task_id}`, {
                    state: { ignoreExitPrompt: true },
                });
            }
            else {
                await updateTaskItems(globalLevel.level_id, toUpload);
                setPersistedData(data);
                setIsQuestionChanged(false);
                if (isPW && !isNew) {
                    const itemsChanged =
                        JSON.stringify(persistedData.items) !== JSON.stringify(data.items);
                    if (itemsChanged) {
                        setMustSaveBeforeAutomarker(false);
                    } else {
                        setMustSaveBeforeAutomarker(true);
                    }
                }
                enqueueSnackbar('Successfully updated task', { variant: 'success' });
            }
        }
        catch (error) {
            console.error(error);
            // If possible, put the validation metadata into state for display.
            const apiResponse = get(error, ['response', 'data']);

            if (apiResponse?.message === 'Unspecified request body' && Array.isArray(apiResponse?.metadata)) {
                setApiValidationErrorData(apiResponse.metadata);
                const hasLengthError = apiResponse.metadata.every(err =>
                    err.key === "description" && err.message.includes("must be between")
                  );
                const hasEmptyError = apiResponse.metadata.every(err =>
                    err.key === "description" && err.message.toLowerCase().includes("empty")
                );
                const hasMissingKeyError = apiResponse.metadata.every(err =>
                    err.key === "answers" && err.message.toLowerCase().includes("one answer must be marked as")
                );
                const hasUnderScoreError = apiResponse.metadata.every(err =>
                    err.key === "items" && err.message.toLowerCase().includes("stimulus_text must have exactly")
                );
                if( hasLengthError || hasEmptyError ){
                    setDescriptionError(true)
                } else if (hasMissingKeyError) {
                    setMissingKeyError(true)
                } else if (hasUnderScoreError) {
                    setUnderScoreError(true)
                } else {
                    enqueueSnackbar('Please complete all required fields', { variant: 'error' })
                }
            } else if (apiResponse?.name === 'DUPLICATE_TASK_ID_NAME_ON_CREATE') {
                apiResponse.metadata = [{ key: 'task_id_name', message: 'Task ID is already in use', path: ['task_id_name'] }];
                setApiValidationErrorData(apiResponse.metadata);
                enqueueSnackbar('Please use a unique Task ID', { variant: 'error' });
            } else {
                enqueueSnackbar('Failed to save task', { variant: 'error' });
            }
        }
        finally {
            setSaving(false);
        }
    };
    // ========= Automarker handlers =========

  const handleSendToAutomarker = async () => {
    if (!isPW) return;
    if (isDirty) {
      setWarningModalOpen(true);
      return;
    }

    if (mustSaveBeforeAutomarker) {
      setWarningModalOpen(true);
      return;
    }
    try {
      await updateArcAutomarkerQuestion(data.task_id);
      setArcReload((prev) => !prev);
      setAutomarkerResponseError(false);
      setAutomarkerResponseModalOpen(true);
      await getTask(data.level_id, data.task_type_id, data.task_id);
      setDirty(false);
      // UI updates
      setShowSendAutoMarkerButton(false);
      setLockedState(false);
      setMustSaveBeforeAutomarker(false);
      setIsSaveButtonTriggered(false);
    } catch (error) {
      const status = error?.response?.status;
      if ([400, 404, 500].includes(status)) {
        setAutomarkerResponseError(true);
        setAutomarkerResponseModalOpen(true);
      }
    }
  };

  const handleReturnToDraft = useCallback(async () => {
      if (!isPW) return;
      setDirty(false);
      // UI: Draft mode
      setShowSendAutoMarkerButton(true);
      setLockedState(true);
      setMustSaveBeforeAutomarker(true);
      setIsSaveButtonTriggered(false);
      setReturnToDraftModalOpen(true);
  }, [isPW]);

    /** @type {TaskContext extends React.Context<infer R> ? R : never} */
    const contextValue = useMemoObject({
        isNew,
        skill,
        taskId,
        taskType,
        data, setData,
        persistedData, setPersistedData,
        apiValidationErrorData,
        registerLocalError,
        isDescriptionError,
        missingKeyError,
        underScoreError,
        lockedState, setLockedState,
        isSaveButtonTriggered, setIsSaveButtonTriggered,
        showSendAutoMarkerButton,
        canEditTaskIdName,
        mustSaveBeforeAutomarker,
    });

    const isProductiveWriting = isPW;
    const activityLogOverrides = isProductiveWriting ? updatedActivityLogStyles : {};

    const ContentEditor = taskType.editor;

    const isNotEditable = useMemo(() => {
        return !!data
            && data.editing_status !== undefined
            && data.editing_status !== 'editable';
    }, [data])

    useEffect(() => {
        if (isNotEditable) {
            enqueueSnackbar('This task is read only as it belongs to a non-draft status test.');
        }
    }, [isNotEditable, enqueueSnackbar])

    const disabled = data?.anchor || isNotEditable || isLoading || isSaving;

    return (
        <TaskContext.Provider value={contextValue}>
            <div className={classes.root}>
                <LoadingScreen open={isLoading} />

                <TaskEditorHeader className={classes.header} save={save} />
                <div style={{ position: 'relative' }}>
                    {/* Add a backdrop to disallow editing in case on 'uneditable' or anchor tasks*/}
                    {disabled &&
                        <Backdrop
                            open={!!(data?.anchor || data?.editing_status === 'uneditable' || data?.editing_status === 'uneditable_speaking')}
                            style={{
                                zIndex: 9001,
                                position: 'absolute',
                                background: 'rgba(0, 0, 0, 0.2)',
                                margin: '-8px',
                                boxShadow: '0 0 2px 2px rgba(0, 0, 0, 0.2)'
                            }}
                        />
                    }
                    {taskType.id === 'productive_writing' && <ARCAutomarkerStatus />}

                    <SectionDividers DividerProps={{ className: classes.divider }}>

                        {!!taskType.backgroundImages && <TaskBackgroundImagesBlock />}

                        {!!data && <ContentEditor />}

                        {taskType.hasEndAudio && <TaskEndAudio />}
                    </SectionDividers>

                    {taskId && !isNew && <SectionDividers DividerProps={{ className: classes.divider }}>
                        <ActivityLogs
                            logId={taskId}
                            logType='task'
                            reload={isSaving}
                            styleOverrides={activityLogOverrides} />
                    </SectionDividers>}
                    { isDescriptionError && <PictureDescriptionModal/>}
                    { missingKeyError && <MissingKeyErrorModal/>}
                    { underScoreError && <InformationTextA2ErrorModal/>}
                    <ScreenSaveCancelBlock
                        save={save}
                        cancelTo={`/${globalLevel.label}/tasks`}
                        disabled={!isDirty || isSaving}
                        showAutofill={process.env.REACT_APP_ENVIRONMENT === 'development'}
                        isInline={taskType?.id === "productive_writing"}
                        customStyles={{ inlineBelowActivityLogs: saveCancelStyles, }}
                    />
                    {taskType?.id === 'productive_writing' && (
                      <SectionDividers>
                        <SendToAutoMarkerButtons
                         disabled={isSaving || isLoading || !!data?.anchor}
                         showSendAutoMarkerButton={showSendAutoMarkerButton}
                         onSendToAutomarker={handleSendToAutomarker}
                         onReturnToDraft={handleReturnToDraft}
                         responseModalOpen={automarkerResponseModalOpen}
                         responseError={automarkerResponseError}
                         onCloseResponseModal={() => setAutomarkerResponseModalOpen(false)}
                         returnToDraftModalOpen={returnToDraftModalOpen}
                         onCloseReturnToDraftModal={() => setReturnToDraftModalOpen(false)}
                         warningModalOpen={warningModalOpen}
                         onCloseWarningModal={() => setWarningModalOpen(false)}
                          />
                      </SectionDividers>
                    )}
                    {taskType?.id === 'productive_writing' && (
                      <SectionDividers>
                       <ARCActivityLog logId={taskId} reload={arcReload} />
                      </SectionDividers>
                    )}
                </div>
            </div>
        </TaskContext.Provider>
    );
}

function normalizeForDirty(task) {
    if (!task) return task;
    const copy = JSON.parse(JSON.stringify(task));

    const normalizeCsvArray = (val) => {
        if (Array.isArray(val)) {
            return val.map(v => String(v).trim()).filter(Boolean);
        }
        if (typeof val === 'string') {
            return val
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
        }
        return [];
    };

    if (Array.isArray(copy.items)) {
        copy.items.forEach(item => {
            if (Array.isArray(item.answers)) {
                item.answers.forEach(ans => {
                    if ('key' in ans) {
                        ans.key = normalizeCsvArray(ans.key);
                    }
                    if ('training_sentences' in ans) {
                        ans.training_sentences = normalizeCsvArray(ans.training_sentences);
                    }
                });
            }
        });
    }

    if (typeof copy.task_id_name === 'string') {
        copy.task_id_name = copy.task_id_name.trim();
    }

    return copy;
}

function normalizeForChangeCheck(task) {
  const t = normalizeForDirty(task);
  if (!t) return t;

  delete t.is_arc_published;
  delete t.editing_status;
  delete t.updated_at;
  delete t.created_at;
  delete t.activity_logs;

  return t;
}

function ARCAutomarkerStatus() {
    const classes = useStyles();
    const { lockedState } = useContext(TaskContext);

    // lockedState: true = Draft (editable), false = Locked (not editable)
    const isDraft = lockedState;
    const statusLabel = isDraft ? 'Draft' : 'Locked';
    const icon = isDraft ? (
        <DraftIcon className={classes.arcAutomarkerIconDraft} />
    ) : (
        <LockIcon className={classes.arcAutomarkerIconLocked} />
    );

    const chipClasses = `${classes.arcAutomarkerChip} ${isDraft ? classes.arcAutomarkerChipDraft : classes.arcAutomarkerChipLocked}`;

    return (
        <div className={classes.arcAutomarkerContainer}>
            <span className={classes.arcAutomarkerLabel}>ARC AI Automarker status</span>
            {lockedState !== null && (
                <Chip
                    label={statusLabel}
                    icon={icon}
                    className={chipClasses}
                    size="small"
                />
            )}
        </div>
    );
}